import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Target, Plus, MagnifyingGlass, CaretDown, CaretRight, PencilSimple, Trash,
  ListPlus, Brain, Tag,
} from "@phosphor-icons/react";

// Couleurs par bucket (fonction commerciale) — palette sombre, accents nets.
const BUCKETS = {
  RECRUITMENT: { label: "Recrutement", color: "#00E5FF" },
  DECISION: { label: "Décision", color: "#F5A623" },
  NEED: { label: "Besoin", color: "#22C55E" },
  PROCUREMENT: { label: "Achat", color: "#FB7185" },
};
const PRIOS = { 1: "P1", 2: "P2", 3: "P3" };
const FAMILIES = ["DEV_BACKEND", "DEV_FRONTEND", "DEV_FULLSTACK", "DEV_MOBILE", "DEV_EMBEDDED", "WEB", "ARCHITECTURE", "QA", "DATA_ENG", "DATA_SCI", "DATA_BI", "AI_ML", "DATABASE", "DEVOPS", "CLOUD", "SYSADMIN", "NETWORK", "SUPPORT", "SECURITY", "ERP", "CRM", "PRODUCT", "PROJECT", "MANAGEMENT"];

const norm = (s) => (s || "").toLowerCase();

export default function AdminDecisionRoles() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState({});

  const [roleModal, setRoleModal] = useState(null);
  const [catModal, setCatModal] = useState(null);
  const [bulkModal, setBulkModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/dm/overview"); setData(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleExpand = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  // ---- role mutations (optimistic-ish: reload after) ----
  const patchRole = async (r, patch) => {
    try { await api.patch(`/admin/dm/roles/${r.id}`, patch); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deleteRole = async (r) => {
    try { await api.delete(`/admin/dm/roles/${r.id}`); toast.success("Rôle supprimé"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleAll = async (cat, active) => {
    try { await api.patch(`/admin/dm/categories/${cat.id}/toggle-all`, { active }); load();
      toast.success(active ? `Tous activés · ${cat.name_fr}` : `Tous désactivés · ${cat.name_fr}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleCategory = async (cat) => {
    try { await api.patch(`/admin/dm/categories/${cat.id}`, { active: !cat.active }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deleteCategory = async (cat) => {
    try { await api.delete(`/admin/dm/categories/${cat.id}`); toast.success("Catégorie supprimée"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // ---- filtering ----
  const filtered = useMemo(() => {
    if (!data) return [];
    const q = norm(search).trim();
    return data.categories.map((c) => {
      let roles = c.roles;
      if (filter === "active") roles = roles.filter((r) => r.active);
      if (filter === "inactive") roles = roles.filter((r) => !r.active);
      if (filter === "priority") roles = roles.filter((r) => r.priority === 1);
      if (q) roles = roles.filter((r) =>
        norm(r.label_fr).includes(q) || norm(r.label_en).includes(q) ||
        (r.aliases || []).some((a) => norm(a).includes(q)));
      return { ...c, _roles: roles };
    }).filter((c) => !q && filter === "all" ? true : c._roles.length > 0);
  }, [data, search, filter]);

  const stats = data?.stats || {};

  return (
    <div data-testid="admin-dm-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 flex-wrap mb-6">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 text-accent mb-2">
            <Brain size={15} weight="fill" /><span className="overline !text-accent">Configuration IA · Ciblage</span>
          </div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Target size={26} weight="duotone" className="text-accent" /> Rôles décideurs</h1>
          <p className="text-muted text-sm mt-2">Définissez les fonctions que l'IA interKO doit rechercher dans les entreprises détectées. Les rôles sont organisés par catégorie, priorisables et extensibles par l'ADMIN.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setCatModal({})} data-testid="admin-dm-add-category"
            className="flex items-center gap-2 text-sm border border-border rounded-md px-3.5 py-2 text-fg hover:border-accent/40 hover-lift">
            <Plus size={14} weight="bold" /> Catégorie
          </button>
          <button onClick={() => setRoleModal({ priority: 2, active: true })} data-testid="admin-dm-add-role"
            className="flex items-center gap-2 text-sm bg-accent text-black font-semibold rounded-md px-4 py-2 hover-lift">
            <Plus size={14} weight="bold" /> Ajouter un rôle
          </button>
        </div>
      </div>

      {/* Stat chips */}
      <div className="flex items-center gap-3 flex-wrap mb-5">
        {[["catégories", stats.categories], ["rôles actifs", stats.roles_active], ["rôles au total", stats.roles_total], ["rôles personnalisés", stats.roles_custom]].map(([lab, val]) => (
          <div key={lab} className="surface rounded-lg px-4 py-2.5 flex items-baseline gap-2" data-testid={`admin-dm-stat-${lab.split(' ')[0]}`}>
            <span className="font-display text-xl font-bold text-accent">{val ?? "—"}</span>
            <span className="text-xs text-muted">{lab}</span>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap mb-5">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-dm-search"
            placeholder="Rechercher un rôle (libellé FR/EN, alias)…" className="bg-surface-2 border-border pl-9" />
        </div>
        <div className="flex items-center gap-1 bg-surface-2 border border-border rounded-md p-1">
          {[["all", "Tous"], ["active", "Actifs"], ["inactive", "Inactifs"], ["priority", "Prioritaires"]].map(([k, lab]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`admin-dm-filter-${k}`}
              className={`text-xs px-3 py-1.5 rounded transition-colors ${filter === k ? "bg-accent text-black font-semibold" : "text-muted hover:text-fg"}`}>{lab}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-muted text-sm animate-pulse">Chargement de la taxonomie…</div>
      ) : (
        <div className="space-y-3" data-testid="admin-dm-categories">
          {filtered.map((c) => {
            const bk = BUCKETS[c.bucket] || BUCKETS.DECISION;
            const isOpen = expanded[c.id] ?? (!!search || filter !== "all");
            return (
              <div key={c.id} data-testid={`dm-category-${c.slug}`}
                className={`surface rounded-lg overflow-hidden border-l-2 ${!c.active ? "opacity-60" : ""}`}
                style={{ borderLeftColor: bk.color }}>
                {/* Category header */}
                <div className="flex items-center gap-3 px-4 py-3">
                  <button onClick={() => toggleExpand(c.id)} className="text-muted hover:text-fg" data-testid={`dm-category-expand-${c.slug}`}>
                    {isOpen ? <CaretDown size={16} /> : <CaretRight size={16} />}
                  </button>
                  <div className="min-w-0 flex-1 cursor-pointer" onClick={() => toggleExpand(c.id)}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-bold text-fg">{c.name_fr}</span>
                      <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded" style={{ color: bk.color, background: `${bk.color}1a`, border: `1px solid ${bk.color}55` }}>{bk.label}</span>
                      {c.created_by_admin && <span className="text-[10px] font-mono text-accent border border-accent/40 rounded px-1">CUSTOM</span>}
                    </div>
                    <div className="text-xs text-muted mt-0.5">{c.name_en} · <span className="text-fg/70">{c.roles_active}/{c.roles_total} actifs</span>{c.applies_all ? " · toutes familles" : (c.families?.length ? ` · ${c.families.slice(0, 3).join(", ")}${c.families.length > 3 ? "…" : ""}` : "")}</div>
                  </div>
                  {/* per-category quick controls */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => toggleAll(c, true)} data-testid={`dm-category-enable-all-${c.slug}`} className="text-[11px] px-2 py-1 rounded border border-border text-muted hover:text-fg hover:border-accent/40">Tout activer</button>
                    <button onClick={() => toggleAll(c, false)} data-testid={`dm-category-disable-all-${c.slug}`} className="text-[11px] px-2 py-1 rounded border border-border text-muted hover:text-fg">Tout désactiver</button>
                    <button onClick={() => setBulkModal({ category_id: c.id, name: c.name_fr, priority: 2 })} data-testid={`dm-category-bulk-${c.slug}`} title="Ajout groupé" className="p-1.5 rounded text-muted hover:text-accent"><ListPlus size={15} /></button>
                    <button onClick={() => setRoleModal({ category_id: c.id, priority: 2, active: true })} data-testid={`dm-category-add-role-${c.slug}`} title="Ajouter un rôle" className="p-1.5 rounded text-muted hover:text-accent"><Plus size={15} /></button>
                    <label className="flex items-center gap-1.5 ml-1 cursor-pointer" title="Catégorie active">
                      <input type="checkbox" checked={c.active} onChange={() => toggleCategory(c)} className="accent-[#00E5FF]" data-testid={`dm-category-active-${c.slug}`} />
                    </label>
                    {c.created_by_admin && c.roles_total === 0 && (
                      <button onClick={() => deleteCategory(c)} className="p-1.5 rounded text-muted hover:text-destructive" title="Supprimer la catégorie"><Trash size={15} /></button>
                    )}
                  </div>
                </div>

                {/* Roles */}
                {isOpen && (
                  <div className="border-t border-border divide-y divide-border/40">
                    {c._roles.length === 0 && <div className="px-4 py-3 text-xs text-muted">Aucun rôle {filter !== "all" ? "correspondant" : ""}.</div>}
                    {c._roles.map((r) => (
                      <RoleRow key={r.id} role={r} onToggle={() => patchRole(r, { active: !r.active })}
                        onPriority={(p) => patchRole(r, { priority: p })}
                        onEdit={() => setRoleModal({ ...r, category_id: c.id, editing: true })}
                        onDelete={() => deleteRole(r)} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {roleModal && <RoleModal state={roleModal} categories={data?.categories || []} onClose={() => setRoleModal(null)} onSaved={() => { setRoleModal(null); load(); }} />}
      {catModal && <CategoryModal onClose={() => setCatModal(null)} onSaved={() => { setCatModal(null); load(); }} />}
      {bulkModal && <BulkModal state={bulkModal} onClose={() => setBulkModal(null)} onSaved={() => { setBulkModal(null); load(); }} />}
    </div>
  );
}

function RoleRow({ role, onToggle, onPriority, onEdit, onDelete }) {
  const [showAliases, setShowAliases] = useState(false);
  return (
    <div className={`flex items-center gap-3 px-4 py-2.5 hover:bg-surface-2/40 ${!role.active ? "opacity-50" : ""}`} data-testid={`dm-role-${role.id}`}>
      <input type="checkbox" checked={role.active} onChange={onToggle} className="accent-[#00E5FF]" data-testid={`dm-role-toggle-${role.id}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-fg">{role.label_fr}</span>
          {role.label_en && role.label_en !== role.label_fr && <span className="text-xs text-muted">· {role.label_en}</span>}
          {role.source_type === "custom" && <span className="text-[9px] font-mono text-accent border border-accent/40 rounded px-1">CUSTOM</span>}
          {(role.aliases || []).length > 0 && (
            <button onClick={() => setShowAliases((s) => !s)} className="text-[10px] text-muted hover:text-accent flex items-center gap-0.5" data-testid={`dm-role-aliases-${role.id}`}>
              <Tag size={10} /> {role.aliases.length} alias
            </button>
          )}
        </div>
        {showAliases && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {role.aliases.map((a, i) => <span key={i} className="text-[10px] font-mono bg-surface-2 border border-border rounded px-1.5 py-0.5 text-muted">{a}</span>)}
          </div>
        )}
      </div>
      {/* priority selector */}
      <div className="flex items-center gap-0.5 shrink-0">
        {[1, 2, 3].map((p) => (
          <button key={p} onClick={() => onPriority(p)} data-testid={`dm-role-priority-${role.id}-p${p}`}
            className={`text-[10px] font-mono w-7 py-1 rounded transition-colors ${role.priority === p
              ? (p === 1 ? "bg-accent text-black font-bold" : p === 2 ? "bg-fg/20 text-fg font-semibold" : "bg-surface-2 text-fg")
              : "text-muted hover:text-fg hover:bg-surface-2"}`}>{PRIOS[p]}</button>
        ))}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button onClick={onEdit} data-testid={`dm-role-edit-${role.id}`} className="p-1.5 rounded text-muted hover:text-fg"><PencilSimple size={14} /></button>
        {role.source_type === "custom" && <button onClick={onDelete} data-testid={`dm-role-delete-${role.id}`} className="p-1.5 rounded text-muted hover:text-destructive"><Trash size={14} /></button>}
      </div>
    </div>
  );
}

const inputCls = "bg-surface-2 border-border text-fg";

function RoleModal({ state, categories, onClose, onSaved }) {
  const [labelFr, setLabelFr] = useState(state.label_fr || "");
  const [labelEn, setLabelEn] = useState(state.label_en || "");
  const [categoryId, setCategoryId] = useState(state.category_id || (categories[0]?.id || ""));
  const [priority, setPriority] = useState(String(state.priority || 2));
  const [active, setActive] = useState(state.active !== false);
  const [aliases, setAliases] = useState((state.aliases || []).join(", "));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!labelFr.trim() && !labelEn.trim()) { toast.error("Indiquez au moins un libellé (FR ou EN)."); return; }
    if (!categoryId) { toast.error("Choisissez une catégorie."); return; }
    setSaving(true);
    const payload = {
      category_id: categoryId, label_fr: labelFr.trim(), label_en: labelEn.trim(),
      priority: Number(priority), active,
      aliases: aliases.split(/[,\n]/).map((a) => a.trim()).filter(Boolean),
    };
    try {
      if (state.editing) await api.patch(`/admin/dm/roles/${state.id}`, payload);
      else await api.post("/admin/dm/roles", payload);
      toast.success(state.editing ? "Rôle mis à jour" : "Rôle ajouté");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-surface border-border max-w-lg" data-testid="dm-role-modal">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Target size={18} className="text-accent" /> {state.editing ? "Modifier le rôle" : "Nouveau rôle décideur"}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Configuration d'un rôle décideur</DialogDescription>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="overline block mb-1.5">Libellé FR</label>
              <Input className={inputCls} value={labelFr} onChange={(e) => setLabelFr(e.target.value)} placeholder="Chargé de recrutement" data-testid="dm-role-label-fr" /></div>
            <div><label className="overline block mb-1.5">Libellé EN</label>
              <Input className={inputCls} value={labelEn} onChange={(e) => setLabelEn(e.target.value)} placeholder="Recruiter" data-testid="dm-role-label-en" /></div>
          </div>
          <div><label className="overline block mb-1.5">Catégorie</label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger className={inputCls} data-testid="dm-role-category"><SelectValue /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name_fr}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="overline block mb-1.5">Priorité</label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className={inputCls} data-testid="dm-role-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">P1 — Cible principale</SelectItem>
                  <SelectItem value="2">P2 — Cible secondaire</SelectItem>
                  <SelectItem value="3">P3 — Cible de repli</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="accent-[#00E5FF]" data-testid="dm-role-active" /> Actif
              </label>
            </div>
          </div>
          <div><label className="overline block mb-1.5">Alias / synonymes (séparés par une virgule)</label>
            <Input className={inputCls} value={aliases} onChange={(e) => setAliases(e.target.value)} placeholder="TA Manager, Responsable recrutement, Talent Recruiter" data-testid="dm-role-aliases-input" />
            <p className="text-[11px] text-muted mt-1">L'IA élargit sa recherche à ces titres équivalents (FR/EN).</p>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="text-sm border border-border rounded-md px-4 py-2 text-muted hover:text-fg">Annuler</button>
            <button onClick={save} disabled={saving} data-testid="dm-role-save" className="text-sm bg-accent text-black font-semibold px-5 py-2 rounded-md hover-lift disabled:opacity-60">{saving ? "Enregistrement…" : "Enregistrer"}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CategoryModal({ onClose, onSaved }) {
  const [nameFr, setNameFr] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [bucket, setBucket] = useState("DECISION");
  const [appliesAll, setAppliesAll] = useState(true);
  const [families, setFamilies] = useState([]);
  const [saving, setSaving] = useState(false);

  const toggleFam = (f) => setFamilies((p) => p.includes(f) ? p.filter((x) => x !== f) : [...p, f]);

  const save = async () => {
    if (!nameFr.trim()) { toast.error("Le nom de la catégorie est requis."); return; }
    setSaving(true);
    try {
      await api.post("/admin/dm/categories", { name_fr: nameFr.trim(), name_en: nameEn.trim(), bucket, applies_all: appliesAll, families: appliesAll ? [] : families });
      toast.success("Catégorie créée"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-surface border-border max-w-lg" data-testid="dm-cat-modal">
        <DialogHeader><DialogTitle className="font-display">Nouvelle catégorie</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Créer une catégorie de rôles décideurs</DialogDescription>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="overline block mb-1.5">Nom FR</label>
              <Input className={inputCls} value={nameFr} onChange={(e) => setNameFr(e.target.value)} placeholder="Finance Leadership" data-testid="dm-cat-name-fr" /></div>
            <div><label className="overline block mb-1.5">Nom EN</label>
              <Input className={inputCls} value={nameEn} onChange={(e) => setNameEn(e.target.value)} placeholder="Finance Leadership" data-testid="dm-cat-name-en" /></div>
          </div>
          <div><label className="overline block mb-1.5">Bucket (fonction commerciale)</label>
            <Select value={bucket} onValueChange={setBucket}>
              <SelectTrigger className={inputCls} data-testid="dm-cat-bucket"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(BUCKETS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={appliesAll} onChange={(e) => setAppliesAll(e.target.checked)} className="accent-[#00E5FF]" data-testid="dm-cat-applies-all" />
            Applicable à toutes les familles de compétences
          </label>
          {!appliesAll && (
            <div><label className="overline block mb-1.5">Familles applicables</label>
              <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                {FAMILIES.map((f) => (
                  <button key={f} onClick={() => toggleFam(f)} className={`text-[10px] font-mono rounded px-1.5 py-1 border ${families.includes(f) ? "bg-accent/15 border-accent/50 text-accent" : "border-border text-muted hover:text-fg"}`}>{f}</button>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="text-sm border border-border rounded-md px-4 py-2 text-muted hover:text-fg">Annuler</button>
            <button onClick={save} disabled={saving} data-testid="dm-cat-save" className="text-sm bg-accent text-black font-semibold px-5 py-2 rounded-md hover-lift disabled:opacity-60">{saving ? "…" : "Créer"}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BulkModal({ state, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [priority, setPriority] = useState("2");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!text.trim()) { toast.error("Saisissez au moins un rôle."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/dm/roles/bulk", { category_id: state.category_id, text, priority: Number(priority) });
      const msg = `${data.created_count} rôle(s) ajouté(s)` + (data.skipped_count ? `, ${data.skipped_count} ignoré(s) (doublons)` : "");
      toast.success(msg);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-surface border-border max-w-lg" data-testid="dm-bulk-modal">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><ListPlus size={18} className="text-accent" /> Ajout groupé — {state.name}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Ajouter plusieurs rôles d'un coup</DialogDescription>
        <div className="space-y-3">
          <div>
            <label className="overline block mb-1.5">Un rôle par ligne</label>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} data-testid="dm-bulk-text"
              className="w-full bg-surface-2 border border-border rounded-md p-3 text-sm text-fg font-mono resize-y focus:outline-none focus:border-accent/50"
              placeholder={"Talent Acquisition Manager\nChargé de recrutement\nResponsable recrutement\nTechnical Recruiter"} />
            <p className="text-[11px] text-muted mt-1">Les doublons (déjà présents ou répétés) sont détectés et ignorés automatiquement.</p>
          </div>
          <div className="w-40"><label className="overline block mb-1.5">Priorité</label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger className={inputCls} data-testid="dm-bulk-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">P1</SelectItem><SelectItem value="2">P2</SelectItem><SelectItem value="3">P3</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="text-sm border border-border rounded-md px-4 py-2 text-muted hover:text-fg">Annuler</button>
            <button onClick={save} disabled={saving} data-testid="dm-bulk-save" className="text-sm bg-accent text-black font-semibold px-5 py-2 rounded-md hover-lift disabled:opacity-60">{saving ? "…" : "Ajouter les rôles"}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
